import { Suspense } from 'react';

import Config from '@config';
import { I18nProvider, initI18nInstance } from '@gasbi/shared/common/providers';
import { NotificationProvider } from '@gasbi/shared/common/providers/NotificationProvider';
import '@gasbi/shared/common/utils/extendYupMethods';
import { ErrorBoundary } from '@gasbi/shared/web/components/ErrorBoundary';
import { ThemeProvider } from '@gasbi/shared/web/providers/ThemeProvider';
import dayjs from 'dayjs';
import 'dayjs/locale/uk';
import { ClickScrollPlugin, OverlayScrollbars } from 'overlayscrollbars';
import 'overlayscrollbars/overlayscrollbars.css';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { RouterProvider } from 'react-router-dom';

import { AuthInitProvider } from '@/providers/AuthInitProvider';
import { ConfirmProvider } from '@/providers/ConfirmDialogProvider';
import {
  FeatureFlagProvider,
  FeatureFlagSwitcher,
} from '@/providers/FeatureFlagsProvider';
import KendoLocaleProvider from '@/providers/KendoLocale';

import { LicenseInfo } from '@mui/x-license';

import GlobalLoadingScreen from '@components/GlobalLoadingScreen';

import { APP_REFRESH_ID } from './app/constants';
import store from './app/store';
import { i18nInstanceOptions } from './locales';
import { getRoutes } from './route';
import './styles/inter-font.css';

OverlayScrollbars.plugin(ClickScrollPlugin);

dayjs.locale('uk');

LicenseInfo.setLicenseKey(Config.VITE_MUI_LICENSE);

localStorage.setItem(APP_REFRESH_ID, Date.now().toString());

async function bootstrap() {
  const i18n = await initI18nInstance(i18nInstanceOptions);

  createRoot(document.getElementById('root')!).render(
    <>
      <ErrorBoundary>
        <I18nProvider instance={i18n}>
          <FeatureFlagProvider>
            <Provider store={store}>
              <ThemeProvider>
                <KendoLocaleProvider>
                  <AuthInitProvider>
                    <Suspense>
                      <NotificationProvider>
                        <ConfirmProvider>
                          <RouterProvider router={getRoutes()} />
                        </ConfirmProvider>
                      </NotificationProvider>
                    </Suspense>
                  </AuthInitProvider>
                </KendoLocaleProvider>
              </ThemeProvider>
            </Provider>
            <FeatureFlagSwitcher />
          </FeatureFlagProvider>
        </I18nProvider>
      </ErrorBoundary>
      <GlobalLoadingScreen />
    </>,
  );
}

bootstrap();

